import { useEffect, useState } from 'react'
import { fetchPlayer, fetchRoster } from '../data/api'
import type { PlayerFile, RosterEntry } from '../types'
import { SectionLabel } from './RoundTimeline'

// Two players side by side.
//
// The player file already returned everything this needs; what was missing was a second column.
// The question it answers is the one a coach asks out loud — "who should be entrying?" — and
// answering it meant opening two tabs and holding one set of numbers in your head.
//
// Every row says which direction is better, because half of these are backwards: more untraded
// deaths is worse, and a comparison that highlights the bigger number would be confidently wrong
// four rows out of eleven.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

type Row = {
  label: string
  /** Higher is better unless this says otherwise. */
  lowerIsBetter?: boolean
  get: (p: PlayerFile) => number | null
  fmt?: (n: number) => string
  hint?: string
}

const ROWS: Row[] = [
  { label: 'Rating', get: (p) => p.totals?.rating ?? null, fmt: (n) => n.toFixed(2) },
  { label: 'ADR', get: (p) => p.totals?.adr ?? null, fmt: (n) => String(Math.round(n)) },
  { label: 'KAST', get: (p) => p.totals?.kast ?? null, fmt: (n) => `${Math.round(n)}%`, hint: 'rounds they did something' },
  { label: 'Headshot %', get: (p) => p.totals?.hsPct ?? null, fmt: (n) => `${Math.round(n)}%` },
  { label: 'Opening kills', get: (p) => p.totals?.openingKills ?? null },
  {
    label: 'Opening duels won',
    get: (p) => {
      const t = p.totals
      if (!t) return null
      const k = t.openingCT.k + t.openingT.k
      const d = t.openingCT.d + t.openingT.d
      return k + d ? Math.round((100 * k) / (k + d)) : null
    },
    fmt: (n) => `${n}%`,
    hint: 'the entry question, and the one people guess at',
  },
  { label: 'AWP kills', get: (p) => p.totals?.awpKills ?? null },
  { label: 'Trade kills', get: (p) => p.totals?.tradeKills ?? null, hint: 'deaths they punished' },
  { label: 'Traded deaths', get: (p) => p.totals?.tradedDeaths ?? null, hint: 'their deaths that were punished for them' },
  {
    label: 'Untraded deaths',
    get: (p) => p.totals?.untradedDeaths ?? null,
    lowerIsBetter: true,
    hint: 'dying alone — lower is better',
  },
  { label: 'Flash assists', get: (p) => p.totals?.util.flashAssists ?? null },
  { label: 'Demos', get: (p) => p.totals?.demos ?? null, hint: 'how much of the above to believe' },
]

const nameOf = (p: PlayerFile) => p.nick ?? p.name

export function Compare({ initial }: { initial?: string }) {
  const [roster, setRoster] = useState<RosterEntry[]>([])
  const [a, setA] = useState(initial ?? '')
  const [b, setB] = useState('')
  const [pa, setPa] = useState<PlayerFile | null>(null)
  const [pb, setPb] = useState<PlayerFile | null>(null)

  useEffect(() => {
    fetchRoster().then(setRoster).catch(() => setRoster([]))
  }, [])
  useEffect(() => {
    if (!a) return setPa(null)
    fetchPlayer(a).then(setPa).catch(() => setPa(null))
  }, [a])
  useEffect(() => {
    if (!b) return setPb(null)
    fetchPlayer(b).then(setPb).catch(() => setPb(null))
  }, [b])

  const picker = (value: string, set: (v: string) => void, label: string, exclude: string) => (
    <select
      value={value}
      onChange={(e) => set(e.target.value)}
      aria-label={label}
      className="border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
      style={{ borderRadius: 'var(--radius)' }}
    >
      <option value="">{label}…</option>
      {roster
        .filter((r) => r.steamid64 !== exclude)
        .map((r) => (
          <option key={r.steamid64} value={r.steamid64}>
            {r.nick ?? r.name}
          </option>
        ))}
    </select>
  )

  return (
    <section className="border border-border bg-surface p-5" style={card}>
      <SectionLabel n="P5" label="Side by side" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {picker(a, setA, 'First player', b)}
        <span className="text-xs text-text-faint">vs</span>
        {picker(b, setB, 'Second player', a)}
      </div>

      {pa && pb ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-text-faint">
                <th className="px-2 py-1.5 text-left font-medium"> </th>
                <th className="px-2 py-1.5 text-right font-medium">{nameOf(pa)}</th>
                <th className="px-2 py-1.5 text-right font-medium">{nameOf(pb)}</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const va = row.get(pa)
                const vb = row.get(pb)
                const fmt = (v: number | null) => (v === null ? '—' : (row.fmt ?? String)(v))
                // Only call a winner when both sides have a number AND they differ. A tie
                // highlighted as a win is the sort of small lie that gets a table distrusted.
                const better =
                  va === null || vb === null || va === vb
                    ? null
                    : row.lowerIsBetter
                      ? va < vb
                        ? 'a'
                        : 'b'
                      : va > vb
                        ? 'a'
                        : 'b'
                const cell = (v: number | null, side: 'a' | 'b') => (
                  <td
                    className="px-2 py-2 text-right tabular-nums"
                    style={{ color: better === side ? 'var(--accent)' : 'var(--text-dim)' }}
                  >
                    {fmt(v)}
                  </td>
                )
                return (
                  <tr key={row.label} className="border-t border-border/60">
                    <td className="px-2 py-2 text-text">
                      {row.label}
                      {row.hint && <span className="ml-2 text-[10px] text-text-faint">{row.hint}</span>}
                    </td>
                    {cell(va, 'a')}
                    {cell(vb, 'b')}
                  </tr>
                )
              })}
            </tbody>
          </table>
          <p className="mt-3 text-[11px] leading-relaxed text-text-faint">
            Highlighted is the better number, which is not always the bigger one — untraded deaths
            reads the other way. Check the demo count before reading much into a gap: two players
            with different numbers of games are not being measured on the same thing.
          </p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-text-faint">
          Pick two. Everything here already existed on each player's own page — this is the version
          you don't have to hold in your head.
        </p>
      )}
    </section>
  )
}
